import api from './api/base';
import { apiHandler } from '../utils/apiUtils';

// ДОБАВЛЕНО: Импорт функций аналитики
import { trackEmailEvent, getCurrentEmailUserData } from '../components/AnalyticsTracker';

// Утилитарная функция для добавления tracking параметров в ссылки
const addTrackingToLinks = (htmlContent, campaignId, emailId = null) => {
    if (!htmlContent) return htmlContent;

    const emailUserData = getCurrentEmailUserData();

    // Регулярное выражение для поиска ссылок
    const linkRegex = /<a\s+([^>]*href\s*=\s*["']([^"']+)["'][^>]*)>/gi;

    return htmlContent.replace(linkRegex, (match, attributes, url) => {
        // Пропускаем ссылки отписки и внутренние якоря
        if (url.includes('unsubscribe') || url.startsWith('#') || url.startsWith('mailto:')) {
            return match;
        }

        // Создаем tracking URL
        const separator = url.includes('?') ? '&' : '?';
        const trackingParams = new URLSearchParams({
            utm_source: 'email',
            utm_medium: 'email_campaign',
            utm_campaign: `campaign_${campaignId}`,
            utm_content: 'email_link',
            email_id: emailId || `email_${Date.now()}`,
            lead_id: emailUserData.leadId || '',
            tracking_click: '1'
        });

        const trackedUrl = `${url}${separator}${trackingParams.toString()}`;

        return `<a ${attributes.replace(url, trackedUrl)} onclick="window.trackEmailLinkClick && window.trackEmailLinkClick('${url}', '${campaignId}')">`;
    });
};

// Управление отправителями
export const fetchEmailSenders = (token, options = {}) => {
    return apiHandler(
        () => api.get('/devtools/email-senders/', {
            headers: { Authorization: `Token ${token}` }
        }),
        {
            defaultValue: [],
            retries: 1,
            ...options
        }
    );
};

export const createEmailSender = (token, senderData, options = {}) => {
    return apiHandler(
        () => api.post('/devtools/email-senders/', senderData, {
            headers: { Authorization: `Token ${token}` }
        }),
        {
            retries: 1,
            ...options
        }
    );
};

export const updateEmailSender = (token, senderId, senderData, options = {}) => {
    return apiHandler(
        () => api.patch(`/devtools/email-senders/${senderId}/`, senderData, {
            headers: { Authorization: `Token ${token}` }
        }),
        options
    );
};

export const deleteEmailSender = (token, senderId, options = {}) => {
    return apiHandler(
        () => api.delete(`/devtools/email-senders/${senderId}/`, {
            headers: { Authorization: `Token ${token}` }
        }),
        {
            defaultValue: true,
            ...options
        }
    );
};

export const setDefaultSender = (token, senderId, options = {}) => {
    return apiHandler(
        () => api.post(`/devtools/email-senders/${senderId}/set_default/`, {}, {
            headers: { Authorization: `Token ${token}` }
        }),
        options
    );
};

// Управление контактами
export const fetchEmailContacts = (token, filters = {}, options = {}) => {
    return apiHandler(
        () => {
            const params = new URLSearchParams();
            Object.keys(filters).forEach(key => {
                if (filters[key]) {
                    params.append(key, filters[key]);
                }
            });

            return api.get(`/devtools/email-contacts/?${params.toString()}`, {
                headers: { Authorization: `Token ${token}` }
            });
        },
        {
            defaultValue: [],
            retries: 1,
            ...options
        }
    );
};

export const fetchEmailContactDetails = (token, contactId, options = {}) => {
    return apiHandler(
        () => api.get(`/devtools/email-contacts/${contactId}/`, {
            headers: { Authorization: `Token ${token}` }
        }),
        options
    );
};

export const createEmailContact = (token, contactData, options = {}) => {
    // Простая валидация на клиенте
    if (!contactData.email?.trim()) {
        throw new Error('Email контакта обязателен');
    }

    return apiHandler(
        () => api.post('/devtools/email-contacts/', contactData, {
            headers: { Authorization: `Token ${token}` }
        }),
        {
            retries: 1,
            ...options
        }
    );
};

export const updateEmailContact = (token, contactId, contactData, options = {}) => {
    return apiHandler(
        async () => {
            const response = await api.patch(`/devtools/email-contacts/${contactId}/`, contactData, {
                headers: { Authorization: `Token ${token}` }
            });

            // Трекинг обновления контакта
            trackEmailEvent('contact_updated', {
                contact_id: contactId,
                updated_fields: Object.keys(contactData),
                event_category: 'contact_management'
            });

            return response;
        },
        options
    );
};

export const deleteEmailContact = (token, contactId, options = {}) => {
    return apiHandler(
        async () => {
            const response = await api.delete(`/devtools/email-contacts/${contactId}/`, {
                headers: { Authorization: `Token ${token}` }
            });

            // Трекинг удаления контакта
            trackEmailEvent('contact_deleted', {
                contact_id: contactId,
                event_category: 'contact_management'
            });

            return response;
        },
        {
            ...options,
            defaultValue: true
        }
    );
};

export const bulkImportContacts = (token, contactsData, updateExisting = false, options = {}) => {
    return apiHandler(
        () => api.post('/devtools/email-contacts/bulk_import/', {
            contacts_data: contactsData,
            update_existing: updateExisting
        }, {
            headers: { Authorization: `Token ${token}` }
        }),
        {
            retries: 1,
            ...options
        }
    );
};


export const fetchContactSegments = (token, options = {}) => {
    return apiHandler(
        () => api.get('/devtools/email-contacts/segments/', {
            headers: { Authorization: `Token ${token}` }
        }),
        {
            ...options,
            defaultValue: []
        }
    );
};

export const fetchContactIndustries = (token, options = {}) => {
    return apiHandler(
        () => api.get('/devtools/email-contacts/industries/', {
            headers: { Authorization: `Token ${token}` }
        }),
        {
            ...options,
            defaultValue: []
        }
    );
};

export const fetchContactTags = (token, options = {}) => {
    return apiHandler(
        () => api.get('/devtools/email-contacts/tags/', {
            headers: { Authorization: `Token ${token}` }
        }),
        {
            ...options,
            defaultValue: []
        }
    );
};

// Быстрая отправка писем
export const fetchQuickEmails = (token, options = {}) => {
    return apiHandler(
        () => api.get('/devtools/quick-emails/', {
            headers: { Authorization: `Token ${token}` }
        }),
        {
            defaultValue: [],
            ...options
        }
    );
};

export const createQuickEmail = (token, emailData, options = {}) => {
    return apiHandler(
        async () => {
            const response = await api.post('/devtools/quick-emails/', emailData, {
                headers: { Authorization: `Token ${token}` }
            });

            // Трекинг создания быстрого письма
            trackEmailEvent('quick_email_created', {
                recipient_email: emailData.recipient_email,
                sender_id: emailData.sender,
                has_html_content: !!emailData.html_content,
                event_category: 'email_marketing'
            });

            return response;
        },
        options
    );
};

export const sendQuickEmail = (token, emailId, options = {}) => {
    return apiHandler(
        async () => {
            const response = await api.post(`/devtools/quick-emails/${emailId}/send_email/`, {}, {
                headers: { Authorization: `Token ${token}` }
            });

            // Трекинг отправки быстрого письма
            trackEmailEvent('quick_email_sent_via_api', {
                email_id: emailId,
                event_category: 'email_marketing'
            });

            return response;
        },
        options
    );
};

export const sendQuickEmailDirect = (token, emailData, options = {}) => {
    // Простая валидация
    if (!emailData.recipient_email?.trim()) {
        throw new Error('Email получателя обязателен');
    }
    if (!emailData.subject?.trim()) {
        throw new Error('Тема письма обязательна');
    }

    // УПРОЩЕНО: убираем клиентский tracking - делаем на бэкенде
    return apiHandler(
        () => api.post('/devtools/quick-emails/send_direct/', emailData, {
            headers: { Authorization: `Token ${token}` }
        }),
        {
            retries: 1,
            ...options
        }
    );
};

export const updateQuickEmail = (token, emailId, emailData, options = {}) => {
    return apiHandler(
        async () => {
            const response = await api.patch(`/devtools/quick-emails/${emailId}/`, emailData, {
                headers: { Authorization: `Token ${token}` }
            });

            // Трекинг обновления быстрого письма
            trackEmailEvent('quick_email_updated', {
                email_id: emailId,
                updated_fields: Object.keys(emailData),
                event_category: 'email_marketing'
            });

            return response;
        },
        options
    );
};

export const deleteQuickEmail = (token, emailId, options = {}) => {
    return apiHandler(
        async () => {
            const response = await api.delete(`/devtools/quick-emails/${emailId}/`, {
                headers: { Authorization: `Token ${token}` }
            });

            // Трекинг удаления быстрого письма
            trackEmailEvent('quick_email_deleted', {
                email_id: emailId,
                event_category: 'email_marketing'
            });

            return response;
        },
        {
            ...options,
            defaultValue: true
        }
    );
};

// Существующие функции для кампаний
export const fetchEmailCampaigns = (token, options = {}) => {
    return apiHandler(
        () => api.get('/devtools/email-campaigns/', {
            headers: { Authorization: `Token ${token}` }
        }),
        {
            defaultValue: [],
            retries: 1,
            ...options
        }
    );
};

export const fetchEmailCampaignDetails = (token, campaignId, options = {}) => {
    return apiHandler(
        () => api.get(`/devtools/email-campaigns/${campaignId}/`, {
            headers: { Authorization: `Token ${token}` }
        }),
        options
    );
};

export const createEmailCampaign = (token, campaignData, options = {}) => {
    // Простая валидация на клиенте
    if (!campaignData.name?.trim()) {
        throw new Error('Название кампании обязательно');
    }
    if (!campaignData.subject?.trim()) {
        throw new Error('Тема письма обязательна');
    }
    if (!campaignData.sender) {
        throw new Error('Необходимо выбрать отправителя');
    }

    if (!campaignData.utm_campaign && campaignData.name) {
        campaignData.utm_campaign = campaignData.name.toLowerCase().replace(/\s+/g, '_');
    }

    return apiHandler(
        () => api.post('/devtools/email-campaigns/', campaignData, {
            headers: { Authorization: `Token ${token}` }
        }),
        {
            retries: 1,
            ...options
        }
    );
};

export const updateEmailCampaign = (token, campaignId, campaignData, options = {}) => {
    // Обновляем tracking в HTML контенте
    if (campaignData.html_content) {
        campaignData.html_content = addTrackingToLinks(
            campaignData.html_content,
            campaignId
        );
    }

    return apiHandler(
        async () => {
            const response = await api.patch(`/devtools/email-campaigns/${campaignId}/`, campaignData, {
                headers: { Authorization: `Token ${token}` }
            });

            // Трекинг обновления кампании
            trackEmailEvent('campaign_api_updated', {
                campaign_id: campaignId,
                updated_fields: Object.keys(campaignData),
                event_category: 'api_calls'
            });

            return response;
        },
        options
    );
};

export const deleteEmailCampaign = (token, campaignId, options = {}) => {
    return apiHandler(
        async () => {
            const response = await api.delete(`/devtools/email-campaigns/${campaignId}/`, {
                headers: { Authorization: `Token ${token}` }
            });

            // Трекинг удаления кампании
            trackEmailEvent('campaign_deleted', {
                campaign_id: campaignId,
                event_category: 'email_marketing'
            });

            return response;
        },
        {
            ...options,
            defaultValue: true
        }
    );
};

export const sendEmailCampaign = (token, campaignId, options = {}) => {
    return apiHandler(
        () => api.post(`/devtools/email-campaigns/${campaignId}/send_campaign/`, {}, {
            headers: { Authorization: `Token ${token}` }
        }),
        {
            retries: 1,
            ...options
        }
    );
};

export const addRecipientsToEmailCampaign = (token, campaignId, recipients, options = {}) => {
    return apiHandler(
        async () => {
            const response = await api.post(`/devtools/email-campaigns/${campaignId}/add_recipients/`, {
                recipients: recipients
            }, {
                headers: { Authorization: `Token ${token}` }
            });

            // Трекинг добавления получателей
            trackEmailEvent('recipients_added_to_campaign', {
                campaign_id: campaignId,
                recipients_count: recipients.length,
                event_category: 'campaign_management'
            });

            return response;
        },
        options
    );
};

export const addRecipientsFromContacts = (token, campaignId, contactIds, options = {}) => {
    return apiHandler(
        async () => {
            const response = await api.post(`/devtools/email-campaigns/${campaignId}/add_recipients_from_contacts/`, {
                contact_ids: contactIds
            }, {
                headers: { Authorization: `Token ${token}` }
            });

            // Трекинг добавления получателей из контактов
            trackEmailEvent('recipients_added_from_contacts', {
                campaign_id: campaignId,
                contacts_count: contactIds.length,
                event_category: 'campaign_management'
            });

            return response;
        },
        options
    );
};

export const fetchEmailCampaignAnalytics = (token, campaignId, options = {}) => {
    return apiHandler(
        () => api.get(`/devtools/email-campaigns/${campaignId}/analytics/`, {
            headers: { Authorization: `Token ${token}` }
        }),
        options
    );
};

export const fetchEmailAnalytics = (token, options = {}) => {
    return apiHandler(
        () => api.get('/devtools/email-analytics/', {
            headers: { Authorization: `Token ${token}` }
        }),
        {
            defaultValue: {
                total_campaigns: 0,
                total_emails_sent: 0,
                total_emails_delivered: 0,
                total_emails_opened: 0,
                total_emails_clicked: 0,
                avg_open_rate: 0,
                avg_click_rate: 0,
                avg_delivery_rate: 0,
                campaigns_by_type: [],
                recent_campaigns: [],
                senders_stats: [],
                contacts_count: 0,
                quick_emails_count: 0
            },
            retries: 1,
            ...options
        }
    );
};

export const fetchEmailRecipients = (token, campaignId = null, options = {}) => {
    return apiHandler(
        () => {
            const params = campaignId ? { campaign_id: campaignId } : {};
            return api.get('/devtools/email-recipients/', {
                params,
                headers: { Authorization: `Token ${token}` }
            });
        },
        {
            ...options,
            defaultValue: []
        }
    );
};

export const fetchEmailTemplates = (token, campaignType = null, options = {}) => {
    return apiHandler(
        () => {
            const url = campaignType
                ? `/devtools/email-templates/by_campaign_type/?campaign_type=${campaignType}`
                : '/devtools/email-templates/';

            return api.get(url, {
                headers: { Authorization: `Token ${token}` }
            });
        },
        {
            ...options,
            defaultValue: []
        }
    );
};

export const createEmailTemplate = (token, templateData, options = {}) => {
    return apiHandler(
        () => api.post('/devtools/email-templates/', templateData, {
            headers: { Authorization: `Token ${token}` }
        }),
        options
    );
};

export const updateEmailTemplate = (token, templateId, templateData, options = {}) => {
    return apiHandler(
        () => api.patch(`/devtools/email-templates/${templateId}/`, templateData, {
            headers: { Authorization: `Token ${token}` }
        }),
        options
    );
};

export const deleteEmailTemplate = (token, templateId, options = {}) => {
    return apiHandler(
        () => api.delete(`/devtools/email-templates/${templateId}/`, {
            headers: { Authorization: `Token ${token}` }
        }),
        {
            ...options,
            defaultValue: true
        }
    );
};

// Oтслеживание кликов и открытий
export const trackEmailClick = (emailId, linkUrl, campaignId = null) => {
    // Отправляем клик в backend для статистики
    return apiHandler(
        () => api.post('/api/devtools/email-tracking/', {
            email_id: emailId,
            campaign_id: campaignId,
            action: 'click',
            url: linkUrl,
            timestamp: new Date().toISOString(),
            user_agent: navigator.userAgent,
            referrer: document.referrer
        }),
        {
            defaultValue: true,
            suppressErrors: true // Не показываем ошибки трекинга пользователю
        }
    );
};

export const trackEmailOpen = (emailId, campaignId = null) => {
    return apiHandler(
        () => api.post('/api/devtools/email-tracking/', {
            email_id: emailId,
            campaign_id: campaignId,
            action: 'open',
            timestamp: new Date().toISOString(),
            user_agent: navigator.userAgent,
            ip_address: 'client' // Backend определит реальный IP
        }),
        {
            defaultValue: true,
            suppressErrors: true
        }
    );
};

// Utility functions для работы с данными
export const validateEmailCampaign = (campaignData) => {
    const errors = [];

    if (!campaignData.name?.trim()) {
        errors.push('Название кампании обязательно');
    }

    if (!campaignData.subject?.trim()) {
        errors.push('Тема письма обязательна');
    }

    if (!campaignData.html_content?.trim() && !campaignData.text_content?.trim()) {
        errors.push('Необходимо указать содержимое письма');
    }

    if (!campaignData.sender) {
        errors.push('Необходимо выбрать отправителя');
    }

    return errors;
};

export const validateEmailRecipients = (recipients) => {
    const errors = [];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!Array.isArray(recipients) || recipients.length === 0) {
        errors.push('Необходимо указать получателей');
        return errors;
    }

    recipients.forEach((recipient, index) => {
        if (!recipient.email) {
            errors.push(`Получатель ${index + 1}: email обязателен`);
        } else if (!emailRegex.test(recipient.email)) {
            errors.push(`Получатель ${index + 1}: некорректный email адрес`);
        }
    });

    return errors;
};

export const validateQuickEmail = (emailData) => {
    const errors = [];

    if (!emailData.recipient_email?.trim()) {
        errors.push('Email получателя обязателен');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailData.recipient_email)) {
        errors.push('Некорректный email получателя');
    }

    if (!emailData.subject?.trim()) {
        errors.push('Тема письма обязательна');
    }

    if (!emailData.html_content?.trim() && !emailData.text_content?.trim()) {
        errors.push('Необходимо указать содержимое письма');
    }

    if (!emailData.sender) {
        errors.push('Необходимо выбрать отправителя');
    }

    return errors;
};

export const parseRecipientsFromText = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const recipients = [];

    lines.forEach(line => {
        const parts = line.split(',').map(part => part.trim());

        if (parts.length >= 1 && parts[0]) {
            recipients.push({
                email: parts[0],
                name: parts[1] || '',
                company: parts[2] || '',
                lead_id: parts[3] || '',
                industry: parts[4] || '',
                segment: parts[5] || ''
            });
        }
    });

    return recipients;
};

export const parseContactsFromText = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    const contacts = [];

    lines.forEach(line => {
        const parts = line.split(',').map(part => part.trim());

        if (parts.length >= 1 && parts[0]) {
            contacts.push({
                email: parts[0],
                name: parts[1] || '',
                company: parts[2] || '',
                phone: parts[3] || '',
                position: parts[4] || '',
                industry: parts[5] || '',
                segment: parts[6] || '',
                tags: parts[7] || '',
                notes: parts[8] || ''
            });
        }
    });

    return contacts;
};

export const getEmailCampaignTemplates = () => ({
    cold_outreach: {
        name: 'Холодная рассылка',
        subject: 'Интересное предложение для {{company}}',
        html_content: `
            <p>Привет, {{name}}!</p>
            <p>Меня зовут [Ваше имя], и я представляю [Ваша компания].</p>
            <p>Мы помогаем компаниям как {{company}} улучшить [проблема которую решаете].</p>
            <p><a href={{tracking_url}}>Узнать больше</a></p>
            <p>С уважением,<br/>[Ваше имя]</p>
        `,
        text_content: `
            Привет, {{name}}!
            
            Меня зовут [Ваше имя], и я представляю [Ваша компания].
            
            Мы помогаем компаниям как {{company}} улучшить [проблема которую решаете].
            
            Узнать больше: {{tracking_url}}
            
            С уважением,
            [Ваше имя]
        `
    },
    technical_newsletter: {
        name: 'Техническая рассылка',
        subject: 'Важные обновления для {{name}}',
        html_content: `
            <h2>Обновления платформы</h2>
            <p>Привет, {{name}}!</p>
            <p>Новые функции и улучшения:</p>
            <ul>
                <li>Улучшение 1</li>
                <li>Улучшение 2</li>
                <li>Улучшение 3</li>
            </ul>
            <p><a href={{tracking_url}}>Попробовать новые функции</a></p>
        `,
        text_content: `
            Обновления платформы
            
            Привет, {{name}}!
            
            Новые функции и улучшения:
            - Улучшение 1
            - Улучшение 2
            - Улучшение 3
            
            Попробовать: {{tracking_url}}
        `
    }
});

if (typeof window !== 'undefined') {
    window.trackEmailLinkClick = (linkUrl, campaignId) => {
        trackEmailClick(null, linkUrl, campaignId);
        trackEmailEvent('email_link_clicked', {
            link_url: linkUrl,
            campaign_id: campaignId,
            event_category: 'email_marketing'
        });
    };
}